import { haversineKm, isValidCoord } from './distance.js';
import { AVAILABILITY } from './stationAvailability.js';

/** Stable id used by the list, the map and focus handling. */
export function stationKey(station) {
  return station?.stationId || station?.chargerCode || null;
}

/**
 * A charger a driver can plausibly walk up to and start: not in maintenance,
 * reports Available, and (when the API tells us connector counts) has a free
 * connector.
 */
export function isUsableNow(station) {
  if (!station || station.maintenanceMode) return false;
  if (station.availability !== AVAILABILITY.AVAILABLE) return false;
  if (Number.isFinite(station.availableConnectors) && station.availableConnectors <= 0) return false;
  return true;
}

/**
 * Pick the charger to send the driver to.
 *
 *   1. nearest charger that is usable now;
 *   2. otherwise (`usable: false`) the nearest charger overall — the caller
 *      shows a note so nobody is told a busy or offline charger is free.
 *
 * Stations without valid coordinates cannot be ranked and are skipped.
 * Ties on distance break by display name so the choice is deterministic.
 *
 * @returns {{ station: object, distanceKm: number, usable: boolean } | null}
 */
export function findNearestCharger(stations, origin) {
  if (!Array.isArray(stations) || !origin || !isValidCoord(origin.lat) || !isValidCoord(origin.lng)) {
    return null;
  }
  const ranked = [];
  for (const s of stations) {
    if (!s || !isValidCoord(s.lat) || !isValidCoord(s.lng)) continue;
    ranked.push({ station: s, distanceKm: haversineKm(origin, { lat: s.lat, lng: s.lng }) });
  }
  if (ranked.length === 0) return null;
  ranked.sort((a, b) => (
    a.distanceKm - b.distanceKm
    || String(a.station.displayName || '').localeCompare(String(b.station.displayName || ''))
  ));
  const usable = ranked.find((r) => isUsableNow(r.station));
  return usable ? { ...usable, usable: true } : { ...ranked[0], usable: false };
}

/** Map a GeolocationPositionError / unsupported browser to driver-facing copy. */
export function geolocationErrorMessage(err) {
  const code = err && typeof err === 'object' ? err.code : undefined;
  if (err === 'unsupported') return "Your browser can't share your location.";
  if (code === 1) return 'Location access is blocked. Allow it in your browser settings to find the nearest charger.';
  if (code === 3 || err === 'timeout') return 'Finding your location took too long.';
  if (code === 2) return "We couldn't work out where you are.";
  return "We couldn't get your location.";
}
